import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const CIEMarksModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  tenantId, 
  academicYearId, 
  programTypeId, 
  programId, 
  termId, 
  semesterId 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [marksData, setMarksData] = useState(null);
  const [showAttendanceInfo, setShowAttendanceInfo] = useState(false);
  const [attendanceSubject, setAttendanceSubject] = useState(null);

  const fetchCIEMarks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        studentId: userId,
        academicYearId: academicYearId,
        programTypeId: programTypeId,
        programId: programId
      });

      if (termId) {
        params.append('termId', termId);
      } else if (semesterId) {
        params.append('semesterId', semesterId);
      }

      const response = await api.get(`/api/studentmarks/marks?${params.toString()}`);
      console.log('✅ CIE Marks fetched:', response.data);
      setMarksData(response.data.data);

    } catch (err) {
      console.error('❌ Error fetching CIE marks:', err);
      setError(err.response?.data?.message || 'Failed to load CIE marks');
    } finally {
      setLoading(false);
    }
  }, [userId, academicYearId, programTypeId, programId, termId, semesterId]);
  
  useEffect(() => {
    if (isOpen && userId && academicYearId && programTypeId && programId) {
      fetchCIEMarks();
    }
  }, [isOpen, userId, academicYearId, programTypeId, programId, fetchCIEMarks]);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  // Determine if term-based or semester-based
  const isTermBased = () => {
    return !!(termId || marksData?.termId);
  };

  const isSemesterBased = () => {
    return !!(semesterId || marksData?.semesterId);
  };

  // Get period label (automatically constructs based on data)
  const getPeriodLabel = () => {
    const periodType = marksData?.studentInfo?.periodType || '';
    const periodName = marksData?.studentInfo?.periodName || '';
    
    if (periodType === 'term') {
      return periodName || 'Term';
    } else if (periodType === 'semester') {
      return periodName || 'Semester';
    }
    
    if (isTermBased()) {
      return marksData?.studentInfo?.termName || 'Term';
    } else if (isSemesterBased()) {
      return marksData?.studentInfo?.semesterName || 'Semester';
    }
    
    return '';
  };

  // Get context display string with meaningful names
  const getContextDisplay = () => {
    const parts = [];
    
    if (marksData?.studentInfo?.programTypeName) {
      parts.push(marksData.studentInfo.programTypeName);
    } else if (marksData?.studentInfo?.programTypeCode) {
      parts.push(marksData.studentInfo.programTypeCode);
    } else if (marksData?.programTypeId) {
      parts.push('PGM');
    }
    
    if (marksData?.studentInfo?.programName) {
      parts.push(marksData.studentInfo.programName);
    } else if (marksData?.studentInfo?.program) {
      parts.push(marksData.studentInfo.program);
    } else if (marksData?.programId) {
      parts.push('PGM');
    }
    
    if (marksData?.studentInfo?.academicYearName) {
      parts.push(marksData.studentInfo.academicYearName);
    } else if (marksData?.studentInfo?.academicYear) {
      parts.push(marksData.studentInfo.academicYear);
    } else if (marksData?.academicYearId) {
      parts.push('AY');
    }
    
    const periodLabel = getPeriodLabel();
    if (periodLabel) {
      parts.push(periodLabel);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'Academic Context';
  };

  // Check if subject has attendance marks
  const hasAttendanceMarks = (subject) => {
    if (!subject?.marks) return false;
    return subject.marks.some(m => 
      m.label && (
        m.label.toLowerCase().includes('attendance') ||
        m.label.toLowerCase().includes('attend')
      )
    );
  };

  // Handle attendance info click
  const handleAttendanceInfoClick = (subject) => {
    setAttendanceSubject(subject);
    setShowAttendanceInfo(true);
  };

  // Attendance Info Modal
  const AttendanceInfoModal = () => {
    if (!showAttendanceInfo || !attendanceSubject) return null;
    
    const attendanceMark = attendanceSubject.marks?.find(m => 
      m.label && (
        m.label.toLowerCase().includes('attendance') ||
        m.label.toLowerCase().includes('attend')
      )
    );

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-base font-bold text-slate-800">Attendance Score Details</h4>
              <p className="text-xs text-slate-500 mt-0.5">{attendanceSubject.subjectName}</p>
            </div>
            <button
              onClick={() => setShowAttendanceInfo(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-medium text-indigo-900">Attendance Score</p>
                <p className="text-base font-bold text-indigo-700">
                  {attendanceMark ? `${attendanceMark.obtained} / ${attendanceMark.max} Marks` : 'Not Available'}
                </p>
              </div>
            </div>
            
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-xs text-slate-600 space-y-2 leading-relaxed">
              <p className="font-semibold text-slate-700">How this score is calculated:</p>
              <ul className="space-y-1.5 list-disc list-inside text-slate-500">
                <li>Calculated using your total attendance records for this term.</li>
                <li>Mapped against the official grading criteria.</li>
                <li>Updates automatically as new attendance is marked.</li>
              </ul>
            </div>
            
            <button
              onClick={() => setShowAttendanceInfo(false)}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-semibold rounded-xl transition-all shadow-sm"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white w-full rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-3xl h-[90vh] sm:h-auto sm:max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
        
        {/* Mobile Pull Handle */}
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto my-2 sm:hidden shrink-0" />

        {/* Header Section */}
        <div className="bg-slate-900 px-5 py-4 sm:px-6 sm:py-5 shrink-0 relative border-b border-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <span className="inline-block px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[10px] font-semibold uppercase tracking-wide mb-1.5">
                CIE Marks Report
              </span>
              <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                Internal Assessment Marks
              </h2>
              
              {/* Dynamic Context Strip */}
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-300">
                <svg className="w-3.5 h-3.5 text-indigo-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="truncate font-medium text-slate-300">
                  {getContextDisplay()}
                </span>
              </div>
              
              {/* Student Info — Hidden/Simplified on mobile */}
              <div className="hidden sm:flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-slate-400">
                <span className="text-white font-semibold">
                  {marksData?.studentInfo?.name || 'Student Marks'}
                </span>
                {marksData?.studentInfo?.usn && (
                  <>
                    <span>•</span>
                    <span className="bg-slate-800 px-1.5 py-0.5 rounded font-mono text-slate-300">
                      {marksData.studentInfo.usn}
                    </span>
                  </>
                )}
                {marksData?.studentInfo?.className && (
                  <>
                    <span>•</span>
                    <span>{marksData.studentInfo.className}</span>
                  </>
                )}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition-colors p-1.5 rounded-lg shrink-0"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Main Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-4 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-9 h-9 rounded-full border-3 border-indigo-600 border-t-transparent animate-spin" />
              <p className="text-xs font-medium text-slate-500 mt-3">Fetching marks...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 px-4 max-w-xs mx-auto">
              <div className="inline-flex p-2.5 rounded-full bg-rose-50 text-rose-500 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-slate-700">{error}</p>
              <button
                onClick={fetchCIEMarks}
                className="mt-3 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm"
              >
                Try Again
              </button>
            </div>
          ) : !marksData || !marksData.subjects || marksData.subjects.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex p-3 rounded-full bg-slate-200/60 text-slate-400 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-700">No Marks Available</p>
              <p className="text-xs text-slate-400 mt-1">Evaluation results haven't been published for this period yet.</p>
            </div>
          ) : (
            /* Subject List Cards */
            <div className="space-y-4">
              {marksData.subjects.map((subject, index) => {
                const hasCie = subject.cieScore && subject.cieScore.max > 0;
                const hasAttendance = hasAttendanceMarks(subject);
                
                return (
                  <div 
                    key={subject.subjectId || index} 
                    className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden"
                  >
                    {/* Subject Card Header */}
                    <div className="p-4 bg-white border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-slate-800 truncate">
                          {subject.subjectName}
                        </h3>
                        {subject.subjectCode && (
                          <p className="text-[11px] font-mono text-slate-400 mt-0.5">
                            {subject.subjectCode}
                          </p>
                        )}
                      </div>
                      
                      {/* Subject Marks Summary */}
                      <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
                        {hasCie ? (
                          <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-indigo-50 text-xs font-bold text-indigo-700 border border-indigo-100">
                            <span className="text-slate-400 font-normal mr-1 text-[10px] uppercase sm:inline hidden">Total CIE:</span>
                            {subject.cieScore.obtained || 0}
                            <span className="text-indigo-300 font-normal mx-0.5">/</span>
                            <span className="text-slate-500 font-normal">{subject.cieScore.max}</span>
                          </div>
                        ) : (
                          <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                            Non-Graded
                          </span>
                        )}
                        
                        {/* Attendance Info Trigger */}
                        {hasAttendance && (
                          <button
                            onClick={() => handleAttendanceInfoClick(subject)}
                            className="text-slate-400 hover:text-indigo-600 bg-slate-50 hover:bg-indigo-50 p-1 rounded-md transition-colors border border-slate-200"
                            title="View attendance details"
                            aria-label="Attendance details"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Marks List Table / Mobile View */}
                    {subject.marks && subject.marks.length > 0 ? (
                      <div className="p-0">
                        
                        {/* Desktop View Table */}
                        <div className="hidden sm:block">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="bg-slate-50/80 border-b border-slate-100 text-slate-400 font-semibold uppercase text-[10px]">
                                <th className="py-2.5 px-4">Component</th>
                                <th className="py-2.5 px-4 text-center w-28">Obtained</th>
                                <th className="py-2.5 px-4 text-center w-28">Max Marks</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-700">
                              {subject.marks.map((mark, idx) => {
                                const isAttendance = mark.label && (
                                  mark.label.toLowerCase().includes('attendance') ||
                                  mark.label.toLowerCase().includes('attend')
                                );
                                return (
                                  <tr key={idx} className="hover:bg-slate-50/50">
                                    <td className="py-2.5 px-4 font-medium text-slate-800 flex items-center gap-2">
                                      {mark.label}
                                      {isAttendance && (
                                        <span className="text-[9px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                          Auto
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2.5 px-4 text-center font-bold text-slate-900 font-mono">{mark.obtained}</td>
                                    <td className="py-2.5 px-4 text-center text-slate-400 font-mono">{mark.max}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile View Stack */}
                        <div className="sm:hidden divide-y divide-slate-100">
                          {subject.marks.map((mark, idx) => {
                            const isAttendance = mark.label && (
                              mark.label.toLowerCase().includes('attendance') ||
                              mark.label.toLowerCase().includes('attend')
                            );
                            return (
                              <div key={idx} className="p-3 flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-slate-700">{mark.label}</span>
                                  {isAttendance && (
                                    <span className="text-[9px] text-indigo-600 bg-indigo-50 px-1 rounded font-semibold">
                                      Auto
                                    </span>
                                  )}
                                </div>
                                <div className="font-mono text-slate-800">
                                  <span className="font-bold">{mark.obtained}</span>
                                  <span className="text-slate-300 mx-1">/</span>
                                  <span className="text-slate-400 text-[11px]">{mark.max}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="py-3 px-4 text-center">
                        <p className="text-xs text-slate-400 italic">No components recorded.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-between shrink-0 text-[11px] text-slate-500 font-medium">
          <span>
            {marksData?.subjects?.length || 0} Course{marksData?.subjects?.length === 1 ? '' : 's'}
          </span>
          <span>
            {isTermBased() ? 'Term System' : isSemesterBased() ? 'Semester System' : ''}
            {marksData?.lastUpdated ? ` • Updated ${new Date(marksData.lastUpdated).toLocaleDateString()}` : ''}
          </span>
        </div>
      </div>

      {/* Attendance Info Modal Component */}
      <AttendanceInfoModal />
    </div>
  );
};

export default CIEMarksModal;